import { Link, useParams, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowLeft, Feather, ArrowUpRight } from 'lucide-react';
import { POSTS } from '@/data/posts';
import { HOME } from '@/constants/testIds';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import Newsletter from '@/components/Newsletter';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

export function FieldNotesIndex() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App">
      <Navigation />
      <main
        data-testid={HOME.fieldNotesIndexPage}
        className="pt-32 pb-20 bg-[var(--brand-parchment)]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            data-testid={HOME.fieldNotesBackLink}
            className="link-expand inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--brand-forest)] mb-10"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>
          <div className="max-w-2xl mb-16">
            <span className="font-eyebrow inline-flex items-center gap-2">
              <Feather size={12} strokeWidth={1.8} /> All Field Notes
            </span>
            <h1 className="mt-3 font-display text-5xl lg:text-7xl text-[var(--brand-forest)] tracking-tight leading-[1.02]">
              Field Notes.
            </h1>
            <p className="mt-5 text-[var(--brand-muted)] text-lg leading-relaxed">
              A slow letter from the west coast. Stories from the beat, kit
              lessons, route notes, adaptive cycling pieces and the odd opinion.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {POSTS.map((p) => (
              <Link
                key={p.slug}
                to={`/field-notes/${p.slug}`}
                data-testid={HOME.fieldNotesCard(p.slug)}
                className="product-card flex flex-col group"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={p.coverImage}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover editorial-img transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute top-3 left-3 bg-[var(--brand-forest)] text-[var(--brand-parchment)] text-[10px] tracking-[0.22em] uppercase px-2.5 py-1">
                    {p.category}
                  </span>
                </div>
                <div className="p-6 flex flex-col grow">
                  <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-[var(--brand-muted)]">
                    <span>{formatDate(p.date)}</span>
                    <span>·</span>
                    <span>{p.readMins} min</span>
                  </div>
                  <h3 className="mt-3 font-display text-2xl text-[var(--brand-forest)] tracking-tight leading-snug">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-[var(--brand-ink)] leading-relaxed grow">
                    {p.excerpt}
                  </p>
                  <span className="mt-5 pt-5 border-t border-[var(--brand-stone)] text-sm font-semibold text-[var(--brand-forest)] inline-flex items-center gap-1.5">
                    Read the note <ArrowUpRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Newsletter />
      <Footer />
    </div>
  );
}

export function FieldNotePost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) return <Navigate to="/field-notes" replace />;

  return (
    <div className="App">
      <Navigation />
      <main
        data-testid={HOME.fieldNotesPostPage}
        className="pt-28 pb-20 bg-[var(--brand-parchment)]"
      >
        <article className="max-w-3xl mx-auto px-6 lg:px-10">
          <Link
            to="/field-notes"
            data-testid={HOME.fieldNotesBackLink}
            className="link-expand inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--brand-forest)]"
          >
            <ArrowLeft size={14} /> All Field Notes
          </Link>

          <div className="mt-10">
            <span className="font-eyebrow">{post.category}</span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--brand-forest)] tracking-tight leading-[1.05]">
              {post.title}
            </h1>
            <div className="mt-5 flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-[var(--brand-muted)]">
              <span>{formatDate(post.date)}</span>
              <span>·</span>
              <span>{post.readMins} minute read</span>
            </div>
          </div>

          <div className="mt-10 aspect-[16/9] overflow-hidden">
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-full object-cover editorial-img"
            />
          </div>

          <div className="mt-12 space-y-6 text-lg leading-[1.75] text-[var(--brand-ink)] font-display">
            {post.body.map((para, i) => (
              <p key={i} className="font-display !font-normal" style={{ fontWeight: 400 }}>
                {para}
              </p>
            ))}
          </div>

          <div className="mt-16 pt-10 border-t border-[var(--brand-stone)]">
            <Link
              to="/#contact"
              className="btn-gold inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold rounded-none"
            >
              Have a question? Drop us a note <ArrowUpRight size={14} />
            </Link>
          </div>
        </article>
      </main>
      <Newsletter />
      <Footer />
    </div>
  );
}
